import { useCallback, useRef } from 'react';

import { partnerScopePickerWording, studentScopePickerWording, WordingFlavor } from 'pages/reports/utils/dialogWording';
import { StudentGMetrixTableDialogHandle } from 'components/StudentGMetrixTableDialog/StudentGMetrixTableDialog';
import { PartnerGMetrixTableDialogHandle } from 'components/PartnerGMetrixTableDialog/PartnerGMetrixTableDialog';
import { getScopeType, needsPartnerPicker, needsStudentPicker } from 'pages/reports/utils/scopePolicy';
import { PartnerTypes } from 'apis/PartnerApi';
import { StudentApi } from 'apis/StudentApi';
import { useIntl } from 'react-intl';

export interface ResolvedScope {
    Id: number;
    Name: string;
}

export interface ScopeResolverContext {

    /** Whether we're opening the picker to select a new scope ('select') or to swap the existing one ('change'). */
    flavor?: WordingFlavor;

    /** The caller's highest role id; used to decide whether to prompt for a partner. */
    highestRoleId: number;

    /** Report's `ScopeId` - dictates which picker (if any) to open. */
    scopeId: number;

    /** The partner selected in the app chrome (the "currently managing" partner). */
    selectedPartner: { Id: number; Name: string; Type?: number };
}

export interface UseScopeResolverOptions {
    partnerPickerRef: React.RefObject<PartnerGMetrixTableDialogHandle>;
    studentPickerRef: React.RefObject<StudentGMetrixTableDialogHandle>;
}

export interface UseScopeResolverResult {
    resolveScope: (ctx: ScopeResolverContext) => Promise<ResolvedScope | null>;
}

export const useScopeResolver = (options: UseScopeResolverOptions): UseScopeResolverResult => {

    const { partnerPickerRef, studentPickerRef } = options;
    const intl = useIntl();

    // Stable refs so the callback identity doesn't churn.
    const partnerRef = useRef(partnerPickerRef);
    const studentRef = useRef(studentPickerRef);
    const intlRef = useRef(intl);
    partnerRef.current = partnerPickerRef;
    studentRef.current = studentPickerRef;
    intlRef.current = intl;

    const resolveScope = useCallback(async (ctx: ScopeResolverContext): Promise<ResolvedScope | null> => {

        const { flavor = 'select', highestRoleId, scopeId, selectedPartner } = ctx;
        const kind = getScopeType(scopeId);

        switch (kind) {

            case 'self':
                return { Id: selectedPartner.Id, Name: selectedPartner.Name };

            case 'partner': {

                // An explicit 'change' always prompts; otherwise reuse a selected partner that already matches the scope type.
                const selectedMatchesScope = flavor === 'select' && selectedPartner.Type === scopeId;
                const prompt = needsPartnerPicker(scopeId, highestRoleId) && !selectedMatchesScope;
                if (prompt) {

                    const result = await partnerRef.current.current?.show({
                        scopeId,
                        ...partnerScopePickerWording(intlRef.current, scopeId, flavor)
                    });
                    if (!result) return null;

                    // For report scope resolution we only need the first selection.
                    const chosen = result.partners.find(p => p.ID === result.ids[0]);
                    if (!chosen) return null;
                    return { Id: chosen.ID, Name: chosen.Name };

                }

                return { Id: selectedPartner.Id, Name: selectedPartner.Name };

            }

            case 'student': {

                const prompt = needsStudentPicker(scopeId);
                if (!prompt) {

                    return { Id: selectedPartner.Id, Name: selectedPartner.Name };

                }

                const result = await studentRef.current.current?.show(studentScopePickerWording(intlRef.current, flavor));
                if (!result) return null;

                // Prefer the student object the picker already selected (works for every role, including
                // paged/server-search roles where GetAll returns nothing without a search string).
                const chosen = result.students?.find(s => s.Id === result.ids[0])
                    ?? (await StudentApi.GetAll(result.search)).find(s => s.Id === result.ids[0]);
                if (!chosen) return null;
                return { Id: chosen.Id, Name: chosen.Fullname };

            }

            default:
                console.warn('[useScopeResolver] unknown scopeId, falling back to active partner', { scopeId });

                return { Id: selectedPartner.Id, Name: PartnerTypes[scopeId] ?? selectedPartner.Name };

        }

    }, []);

    return { resolveScope };

};
